import { useState } from 'react';
import { CustomSelectBase } from '@ardoq/dropdown-menu';
import { actionNamespaceSelectParent } from '@ardoq/global-consts';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { dispatchAction } from '@ardoq/rxbeach';
import { parentSelected, ParentSelectedPayload } from '../../actions';
import { setFilterTerm } from '../../actions/navigatorActions';
import { useDispatchWorkspaceQueryOnOpenDropdown } from '../streams/useDispatchWorkspaceQueryOnOpenDropdown';
import { SelectParentRoot } from '../SelectParentRoot';
import { Locale } from '@ardoq/locale';

export type ParentValue = {
  id: string;
  name: string;
} | null;

export type ParentSelectorProps = {
  // The component ids are needed so we can exclude any descendants as an optional parent.
  componentIds: string[];
  // Only a component of the root workspace of the component can be a parent.
  workspaceId: string;
  // undefined = nothing selected yet, null = root selected (no parent)
  parent: ParentValue | undefined;
  // Displayed in the input when the user selects root (no parent)
  rootWorkspaceName?: string;
  onParentSelected: (value: ParentValue) => void;
  locale: Locale;
  i18nLocale?: I18nLocale;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

export const ParentSelector = ({
  workspaceId,
  componentIds,
  parent,
  rootWorkspaceName,
  onParentSelected,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  onMenuOpen,
  onMenuClose,
}: ParentSelectorProps) => {
  const [currentParent, setCurrentParent] = useState(parent);

  useDispatchWorkspaceQueryOnOpenDropdown(
    workspaceId,
    componentIds,
    currentParent ?? null
  );

  const onOptionsChanged = (values: ParentSelectedPayload) => {
    const parent = values
      ? {
          id: values.parentId,
          name: values.parentName,
        }
      : null;
    onParentSelected(parent);
    setCurrentParent(parent);
  };

  const onClear = () => {
    onOptionsChanged(null);
    setCurrentParent(null);
  };

  const onFilterChanged = (filterTerm: string) =>
    dispatchAction(setFilterTerm(filterTerm), actionNamespaceSelectParent);

  return (
    <CustomSelectBase
      placeholderText={t(
        i18nLocale,
        'componentHierarchies:parentSelector.placeholder',
        'Select...'
      )}
      onOptionsChanged={onOptionsChanged}
      optionsChangedAction={parentSelected}
      actionNamespace={actionNamespaceSelectParent}
      onFilterChanged={onFilterChanged}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      closeOnSelect={true}
      defaultValue={
        currentParent === undefined
          ? undefined
          : currentParent === null
            ? rootWorkspaceName
            : currentParent.name
      }
      hasClearButton={true}
      onClear={onClear}
    >
      <SelectParentRoot i18nLocale={i18nLocale} locale={locale} />
    </CustomSelectBase>
  );
};
